import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTeam } from '@/contexts/TeamContext';
import { useSessionReady } from '@/hooks/useSessionReady';
import { prefetchAllModules } from '@/lib/backgroundPrefetch';

const REFRESH_MIN_INTERVAL_MS = 5 * 60 * 1000; // 5 min throttle

/**
 * Mounts once near the top of the tree and silently warms the React Query
 * cache for every module's primary dataset (stock items + packages, recipes,
 * meal plans) as soon as the team is known. When the user clicks a tab, the
 * data is already in cache so the view renders without a fetch round-trip.
 *
 * Cache-first: the hooks themselves still hydrate from localStorage via
 * `placeholderData`, so even on first cold start the user sees data instantly
 * while this prefetcher refreshes it in the background.
 *
 * Throttled: re-prefetches at most every 5 minutes per team, and on tab
 * visibility return (also throttled). Never blocks the UI.
 */
export function BackgroundPrefetcher(): null {
  const { currentTeamId } = useTeam();
  const sessionReady = useSessionReady();
  const queryClient = useQueryClient();
  const lastRunRef = useRef<Record<string, number>>({});

  useEffect(() => {
    if (!currentTeamId || !sessionReady) return;

    const run = () => {
      const now = Date.now();
      const last = lastRunRef.current[currentTeamId] || 0;
      if (now - last < REFRESH_MIN_INTERVAL_MS) return;
      lastRunRef.current[currentTeamId] = now;
      // Fire and forget — explicit catch so unhandled rejections don't surface.
      prefetchAllModules(queryClient, currentTeamId).catch((err) => {
        console.warn('[BackgroundPrefetcher] failed', err);
      });
    };

    // Initial warm-up on team load. Defer slightly so we don't compete with
    // critical first-paint queries (tasks, tags, family).
    const t = window.setTimeout(run, 1500);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') run();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearTimeout(t);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [currentTeamId, sessionReady, queryClient]);

  return null;
}